import {
  CaretDownIcon,
  CurrencyCircleDollarIcon,
  CreditCardIcon,
  GearIcon,
  SignOutIcon,
  CheckIcon
} from '@phosphor-icons/react';
import LogoCircle from '../../public/images/logos/logo_circle.svg';
import LogoText from '../../public/images/logos/logo_text.svg';
import Avatar from '@atoms/Avatar';
import Tooltip from '@components/Presentation/atoms/Tooltip';
import { Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from '@atoms/new/dropdown';
import Button from '@atoms/new/button';
import Badge from '@atoms/Badge';
import { getFormattedPlanName } from '@components/PricingPlans/helper';

interface TeamSwitcherProps {
  isExpanded: boolean;
  isPowerpoint: boolean;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (updater: (prev: boolean) => boolean) => void;
  teamName?: string;
  currentTeam: any;
  teams: any[];
  planName: string;
  router: any;
  selectTeam: (team: any) => void;
  doLogout: () => Promise<void>;
  onOpenWorkspaceSettings: () => void;
  getActiveUcc: () => Promise<void>;
}

export default function TeamSwitcher({
  isExpanded,
  isPowerpoint,
  isDropdownOpen,
  setIsDropdownOpen,
  teamName,
  currentTeam,
  teams,
  planName,
  router,
  selectTeam,
  doLogout,
  onOpenWorkspaceSettings,
  getActiveUcc
}: TeamSwitcherProps) {
  return (
    <div>
      <div className='flex gap-2 h-16 items-center'>
        {isExpanded && (
          <>
            <LogoText className="ml-9 text-grey-900 dark:text-white" />
            {isPowerpoint && <Badge variant="warning" >Beta</Badge>}
          </>
        )}

        {!isExpanded && <LogoCircle className="mx-auto" />}
      </div>

      <div className={`${isExpanded ? 'px-6' : ''} border-t border-b border-divider `}>
        <Dropdown
          placement='bottom-start'
          isOpen={isDropdownOpen}
          className='w-[300px]'
          onClose={() => {
            setIsDropdownOpen(() => false);
          }}>
          <DropdownTrigger>
            <button
              className={`aria-expanded:scale-[1] w-full focus-visible:outline-none cursor-pointer my-1 rounded-lg ${isExpanded && 'hover:bg-content3 active:bg-content3'}`}
              onClick={() => {
                setIsDropdownOpen(prev => !prev);
              }}>
              <div className={`${isExpanded ? 'px-2' : 'px-2'} py-1 flex items-center`}>
                <div className='flex-1 overflow-hidden'>
                  <div className='font-medium text-sm flex gap-2 items-center justify-center'>
                    <Avatar
                      w='8'
                      h='8'
                      isProfile
                      name={teamName} />
                    <div className={`${isExpanded ? 'flex flex-col flex-wrap flex-1 text-left truncate' : 'hidden'}`}>
                      <span className='truncate w-full'>{isExpanded && teamName && teamName}</span>
                      <div className='flex'>
                        <Badge className='uppercase'>
                          {getFormattedPlanName(planName).split('(')[0]}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                {isExpanded && <div className="cursor-pointer">
                  <CaretDownIcon size={16} />
                </div>}
              </div>
            </button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Static Actions"
            disallowEmptySelection
            selectionMode="single"
            selectedKeys={[currentTeam?.id || '']}
          >
            <DropdownSection showDivider
              className="m-0!"
              classNames={{
                divider: 'mt-1 mb-2',
              }}>
              <DropdownItem className='data-[hover=true]:bg-transparent! data-[hover=true]:cursor-default'>
                <p className='text-sm font-normal text-default-500'>
                  {currentTeam?.email}
                </p>
              </DropdownItem>
            </DropdownSection>
            <DropdownSection showDivider
              className="m-0!"
              classNames={{
                divider: 'mt-1 mb-2',
              }}>
              <>
                {!isPowerpoint && (
                  <DropdownItem
                    key="refer-a-friend"
                    startContent={<CurrencyCircleDollarIcon size={20} />}
                    onAction={getActiveUcc}
                    className={'celloClass'}
                  >
                    <div className='flex items-center justify-between w-full'>
                      <span>Refer a friend</span>
                      <Badge>Earn $100</Badge>
                    </div>
                    <div className='announcement'></div>
                  </DropdownItem>
                )}
                <DropdownItem
                  key="pricing"
                  startContent={<CreditCardIcon size={20} />}
                  onAction={() => {
                    router.push('/pricing');
                    setIsDropdownOpen(() => false);
                  }}
                >
                  Billing and plan
                </DropdownItem>
                <DropdownItem
                  key="settings"
                  startContent={<GearIcon size={20} />}
                  onAction={() => {
                    router.push('/settings');
                    setIsDropdownOpen(() => false);
                  }}
                >
                  Settings
                </DropdownItem>
              </>
            </DropdownSection>
            <DropdownSection title='WORKSPACE' id='workspace-section'
              className="m-0!"
              classNames={{
                group: 'max-h-32 overflow-auto border-b border-divider py-1',
                heading: 'sticky top-0 bg-content2 z-10',
              }}>
              {teams.map((team) => (
                <DropdownItem
                  className='h-12'
                  endContent={currentTeam?.id === team.id ?
                    <Tooltip
                      content='Workspace settings'
                    >
                      <Button
                        kind='icon-only'
                        size='sm'
                        className='relative right-0 '
                      >
                        <GearIcon size={20}
                          onClick={() => {
                            onOpenWorkspaceSettings();
                            setIsDropdownOpen(() => false);
                          }}
                        />
                      </Button>
                    </Tooltip> : null}
                  startContent={currentTeam?.id === team.id ? <CheckIcon size={20} /> : <div className='w-5' />}
                  selectedIcon={() => null}
                  key={team.id}
                  onAction={() => {
                    if (team.id === currentTeam?.id) return;
                    selectTeam(team);
                    setIsDropdownOpen(() => false);
                    router.push('/');
                  }}
                >
                  <div className='flex items-start flex-col w-full'>
                    <span className='truncate'>{team.billing_account.name}</span>
                    {team.billing_account.purchase_plan?.name && (
                      <Badge className='uppercase'>
                        {getFormattedPlanName(team.billing_account.purchase_plan.name).split('(')[0]}
                      </Badge>
                    )}
                  </div>
                </DropdownItem>

              ))}
            </DropdownSection>
            <DropdownItem
              key="delete"
              onAction={async () => {
                await doLogout();
                router.push('/login');
              }}
            >
              <p className='text-red-500 flex gap-2'>
                <SignOutIcon size={20} /> Log out
              </p>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
}
